import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import withTheme from '../styles/withTheme'
import Icon from '../Icon'

import './rf-button.scss'

const Button = forwardRef(function Button(props, ref) {
	const {
		children,
		className,
		color,
		component: Component = 'button',
		disabled,
		fullWidth,
		icon,
		IconProps,
		onClick,
		showLoading,
		size,
		theme,
		variant,
		...other
	} = props

	const handleClick = (e) => {
		!disabled && onClick && onClick(e)
	}

	return (
		<Component
			ref={ref}
			className={clsx(
				'rf-button',
				`rf-button--${variant || 'naked'}`,
				color && `rf-button--${variant || 'naked'}-${color}`,
				size && `rf-button--${size}`,
				fullWidth && 'rf-button--full-width',
				children && (icon /*|| showLoading*/) && 'rf-button--with-icon',
				!children && (icon || showLoading) && 'rf-button--icon',
				disabled && 'rf-button--disabled',
				className
			)}
			type="button"
			disabled={disabled}
			onClick={handleClick}
			{...other}
		>
			{children && <span className="rf-button-label" style={{ pointerEvents: 'none' }}>{children}</span>}
			{icon && !showLoading && (
				<span className="rf-button-icon">
					<Icon {...IconProps}>{icon}</Icon>
				</span>
			)}
			{showLoading && (
				<span className={clsx(
					icon ? 'rf-button-icon' : 'rf-button-icon-absolute'
				)}>
					<Icon {...IconProps}>
						<circle cx="120" cy="512" r="120">
							<animate
								attributeName="r"
								values="70;120;70"
								dur="1.25s"
								begin="0s"
								repeatCount="indefinite"
							/>
							<animate
								attributeName="opacity"
								values="0.2;1;0.2"
								dur="1.25s"
								begin="0s"
								repeatCount="indefinite"
							/>
						</circle>
						<circle cx="512" cy="512" r="120">
							<animate
								attributeName="r"
								values="70;120;70"
								dur="1.25s"
								begin="0.25s"
								repeatCount="indefinite"
							/>
							<animate
								attributeName="opacity"
								values="0.2;1;0.2"
								dur="1.25s"
								begin="0.25s"
								repeatCount="indefinite"
							/>
						</circle>
						<circle cx="904" cy="512" r="120">
							<animate
								attributeName="r"
								values="70;120;70"
								dur="1.25s"
								begin="0.5s"
								repeatCount="indefinite"
							/>
							<animate
								attributeName="opacity"
								values="0.2;1;0.2"
								dur="1.25s"
								begin="0.5s"
								repeatCount="indefinite"
							/>
						</circle>
					</Icon>
				</span>
			)}
		</Component>
	)
})

export default withTheme('rf-button')(Button)

Button.defaultProps = {
	disabled: false,
	fullWidth: false,
	size: 'medium',
	color: 'primary',
	variant: 'naked'
}

Button.propTypes = {
	/**
	 * The content of the button.
	 */
	children: PropTypes.node,

	/**
	 * If `true`, the button is disabled.
	 * @default false
	 */
	disabled: PropTypes.bool,

	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * @default 'button'
	 */
	component: PropTypes.elementType,

	/**
	 * If `true`, the button is full width.
	 * @default false
	 */
	fullWidth: PropTypes.bool,

	/**
	 * The size of the button.
	 * `small` is equivalent to the dense button styling.
	 * @default 'primary'
	 */
	color: PropTypes.oneOf(['primary', 'secondary', 'dark', 'error']),

	/**
	 * Props applied to the icon element.
	 */
	IconProps: PropTypes.object,

	/**
	 * The size of the button.
	 * `small` is equivalent to the dense button styling.
	 * @default 'medium'
	 */
	size: PropTypes.oneOf(['large', 'medium', 'small']),

	/**
	 * If `true`, a loading icon is showed.
	 * @default false
	 */
	showLoading: PropTypes.bool,

	/**
	 * The variant to use.
	 * @default 'text'
	 */
	variant: PropTypes.oneOfType([PropTypes.oneOf(['fill', 'ghost', 'naked', 'link']), PropTypes.string]),

	/**
	 * Icon placed after the children.
	 */
	icon: PropTypes.string
}
